<script>
import Modal from '../../Modal.vue';
import MultiselectDropdown from 'shared/components/ui/MultiselectDropdown.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import { mapGetters } from 'vuex';

export default {
  components: {
    Modal,
    MultiselectDropdown,
    NextButton,
  },
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    targetBranch: {
      type: Object,
      required: true,
    },
  },
  emits: ['close', 'confirm'],
  data() {
    return {
      selectedTeam: null,
      selectedAgent: null,
      isFetchingTeamMembers: false,
    };
  },
  computed: {
    ...mapGetters({
      teams: 'teams/getTeams',
      agents: 'agents/getAgents',
    }),
    teamsList() {
      let filteredTeams = this.teams;

      if (this.targetBranch && this.targetBranch.id) {
        filteredTeams = filteredTeams.filter(
          team => team.branch_id === this.targetBranch.id
        );
      } else {
        filteredTeams = filteredTeams.filter(team => !team.branch_id);
      }

      return [
        { id: 0, name: this.$t('TEAMS_SETTINGS.LIST.NONE') },
        ...filteredTeams,
      ];
    },
    agentsList() {
      let filteredAgents = this.agents;

      // Filter by target branch (agent must have access to the target branch)
      if (this.targetBranch && this.targetBranch.id) {
        filteredAgents = filteredAgents.filter(
          agent =>
            agent.role !== 'agent' ||
            (agent.branch_ids &&
              agent.branch_ids.includes(this.targetBranch.id))
        );
      }

      // If a team is selected, filter agents by that team
      if (this.selectedTeam) {
        const teamMembers = this.$store.getters['teamMembers/getTeamMembers'](
          this.selectedTeam.id
        );
        const teamMemberIds = teamMembers.map(member => member.id);
        filteredAgents = filteredAgents.filter(agent =>
          teamMemberIds.includes(agent.id)
        );
      }

      return [
        {
          id: 0,
          name: this.$t('AGENT_MGMT.MULTI_SELECTOR.SEARCH.PLACEHOLDER.AGENT'),
        },
        ...filteredAgents,
      ];
    },
    isSubmitDisabled() {
      return !this.selectedTeam && !this.selectedAgent;
    },
  },
  watch: {
    show(newVal) {
      if (newVal) {
        this.selectedTeam = null;
        this.selectedAgent = null;
        this.isFetchingTeamMembers = false;
      }
    },
  },
  methods: {
    onClose() {
      this.$emit('close');
    },
    onConfirm() {
      if (this.isSubmitDisabled) return;
      this.$emit('confirm', {
        team: this.selectedTeam,
        agent: this.selectedAgent,
      });
      this.onClose();
    },
    onSelectTeam(team) {
      this.selectedTeam = team && team.id !== 0 ? team : null;
      if (this.selectedTeam) {
        this.isFetchingTeamMembers = true;
        this.$store
          .dispatch('teamMembers/get', { teamId: this.selectedTeam.id })
          .then(() => {
            if (this.selectedAgent) {
              const teamMembers = this.$store.getters[
                'teamMembers/getTeamMembers'
              ](this.selectedTeam.id);
              const isMember = teamMembers.some(
                member => member.id === this.selectedAgent.id
              );
              if (!isMember) {
                this.selectedAgent = null;
              }
            }
          })
          .finally(() => {
            this.isFetchingTeamMembers = false;
          });
      }
    },
    onSelectAgent(agent) {
      this.selectedAgent = agent && agent.id !== 0 ? agent : null;
    },
  },
};
</script>

<template>
  <Modal :show="show" :on-close="onClose">
    <div class="h-auto overflow-auto flex flex-col">
      <woot-modal-header
        :title="$t('CONVERSATION.BRANCH_TRANSFER_MODAL.TITLE')"
        :content="$t('CONVERSATION.BRANCH_TRANSFER_MODAL.DESCRIPTION')"
      />
      <div class="flex flex-col gap-4 py-4 px-6 w-full">
        <div>
          <label class="text-sm font-medium text-n-slate-12">{{
            $t('CONVERSATION.BRANCH_TRANSFER_MODAL.TARGET_BRANCH')
          }}</label>
          <div
            class="text-sm text-n-slate-11 mt-1 p-2 bg-n-slate-2 rounded border border-n-slate-4"
          >
            {{
              targetBranch
                ? targetBranch.name
                : $t('CONVERSATION.BRANCH_TRANSFER_MODAL.NONE')
            }}
          </div>
        </div>

        <div>
          <label class="text-sm font-medium text-n-slate-12">{{
            $t('CONVERSATION.BRANCH_TRANSFER_MODAL.TEAM')
          }}</label>
          <MultiselectDropdown
            :options="teamsList"
            :selected-item="selectedTeam"
            :multiselector-title="$t('AGENT_MGMT.MULTI_SELECTOR.TITLE.TEAM')"
            :multiselector-placeholder="
              $t('AGENT_MGMT.MULTI_SELECTOR.PLACEHOLDER')
            "
            :no-search-result="
              $t('AGENT_MGMT.MULTI_SELECTOR.SEARCH.NO_RESULTS.TEAM')
            "
            :input-placeholder="
              $t('AGENT_MGMT.MULTI_SELECTOR.SEARCH.PLACEHOLDER.TEAM')
            "
            @select="onSelectTeam"
          />
        </div>

        <div>
          <label class="text-sm font-medium text-n-slate-12">{{
            $t('CONVERSATION.BRANCH_TRANSFER_MODAL.AGENT')
          }}</label>
          <MultiselectDropdown
            :options="agentsList"
            :selected-item="selectedAgent"
            :multiselector-title="$t('AGENT_MGMT.MULTI_SELECTOR.TITLE.AGENT')"
            :multiselector-placeholder="
              isFetchingTeamMembers
                ? $t('CONVERSATION.BRANCH_TRANSFER_MODAL.LOADING_MEMBERS')
                : $t('AGENT_MGMT.MULTI_SELECTOR.PLACEHOLDER')
            "
            :no-search-result="
              $t('AGENT_MGMT.MULTI_SELECTOR.SEARCH.NO_RESULTS.AGENT')
            "
            :input-placeholder="
              isFetchingTeamMembers
                ? $t('CONVERSATION.BRANCH_TRANSFER_MODAL.LOADING')
                : $t('AGENT_MGMT.MULTI_SELECTOR.SEARCH.PLACEHOLDER.AGENT')
            "
            @select="onSelectAgent"
          />
        </div>
      </div>

      <div
        class="flex flex-row justify-end gap-2 py-4 px-6 w-full bg-n-slate-1 border-t border-n-slate-3"
      >
        <NextButton
          faded
          type="reset"
          :label="$t('CONVERSATION.BRANCH_TRANSFER_MODAL.CANCEL')"
          @click="onClose"
        />
        <NextButton
          type="submit"
          :label="$t('CONVERSATION.BRANCH_TRANSFER_MODAL.SUBMIT')"
          :disabled="isSubmitDisabled"
          @click="onConfirm"
        />
      </div>
    </div>
  </Modal>
</template>
